import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Lock, ShoppingBag, TicketPercent, X } from "lucide-react";
import { Header } from "@/components/Header";
import { api, thumbUrl, brl, errMsg } from "@/lib/api";
import { useCart } from "@/lib/cart";

export default function Cart() {
  const { items, remove, total, subtotal, groups } = useCart();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const discount = coupon ? Math.round(total * coupon.percent) / 100 : 0;
  const finalTotal = total - discount;

  const applyCoupon = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code });
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado: ${data.percent}% off`);
    } catch (err) {
      setCoupon(null);
      toast.error(errMsg(err));
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        photo_ids: items.map((i) => i.id),
        email,
        name,
        coupon: coupon?.code || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <span className="label-caps text-zinc-500">Carrinho</span>
        <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter">Suas fotos</h1>
        {items.length === 0 ? (
          <div className="mt-12 text-zinc-600" data-testid="cart-empty">
            <ShoppingBag className="w-8 h-8 mb-3" />
            Seu carrinho está vazio. <Link to="/" className="text-[#0055FF] underline">Encontrar minhas fotos</Link>
          </div>
        ) : (
          <div className="mt-8 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.id} className="relative aspect-[4/5] rounded-md overflow-hidden bg-zinc-100" data-testid={`cart-item-${i.id}`}>
                  <img src={thumbUrl(i.id)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="absolute top-1.5 right-1.5 w-8 h-8 rounded-md bg-white/90 grid place-items-center hover:bg-white">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                  <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs px-2 py-1 truncate">{brl(i.price)}</span>
                </div>
              ))}
            </div>
            <form onSubmit={pay} className="lg:col-span-5 border border-zinc-200 rounded-lg p-6 h-fit lg:sticky lg:top-24" data-testid="checkout-form">
              <div className="flex justify-between text-sm text-zinc-600"><span>{items.length} foto(s)</span><span>{brl(subtotal)}</span></div>
              {groups.filter((g) => g.packaged).map((g) => (
                <div key={g.event_id} className="mt-2 flex justify-between text-sm text-[#0055FF]" data-testid={`cart-package-${g.event_id}`}>
                  <span>Pacote {g.event_name} ({g.count} fotos)</span><span>-{brl(g.sum - g.total)}</span>
                </div>
              ))}
              {coupon && (
                <div className="mt-2 flex justify-between text-sm text-[#0055FF]" data-testid="cart-coupon-line">
                  <span className="flex items-center gap-1">
                    Cupom {coupon.code} ({coupon.percent}%)
                    <button type="button" onClick={() => setCoupon(null)} data-testid="coupon-remove-btn"><X className="w-3.5 h-3.5" /></button>
                  </span>
                  <span>-{brl(discount)}</span>
                </div>
              )}
              {!coupon && (
                <div className="mt-4 flex gap-2">
                  <div className="relative flex-1">
                    <TicketPercent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Cupom de desconto" data-testid="coupon-input" className="w-full h-11 pl-9 pr-3 rounded-md border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0055FF]" />
                  </div>
                  <button type="button" onClick={applyCoupon} data-testid="coupon-apply-btn" className="h-11 px-4 rounded-md border border-zinc-900 text-sm font-semibold hover:bg-zinc-900 hover:text-white transition-colors">Aplicar</button>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-zinc-200 flex justify-between font-display text-2xl font-bold">
                <span>Total</span><span data-testid="cart-total">{brl(finalTotal)}</span>
              </div>
              <label className="block mt-6 text-sm font-medium">Nome</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="checkout-name-input" className="mt-1 w-full h-12 px-3 rounded-md border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0055FF]" />
              <label className="block mt-4 text-sm font-medium">Email (para receber o link das fotos)</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email-input" className="mt-1 w-full h-12 px-3 rounded-md border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0055FF]" />
              <button disabled={loading} data-testid="checkout-submit-btn" className="mt-6 w-full h-12 rounded-md bg-[#0055FF] hover:bg-[#0040CC] disabled:opacity-60 text-white font-semibold flex items-center justify-center gap-2 transition-colors">
                <Lock className="w-4 h-4" /> {loading ? "Redirecionando…" : `Pagar ${brl(finalTotal)}`}
              </button>
              <p className="mt-3 text-xs text-zinc-500">Pague com Pix ou cartão, com segurança via Stripe. Após a confirmação, você baixa as fotos em alta resolução, sem marca d'água.</p>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
